import { DefaultValues } from 'react-hook-form'

import { BookingDTO, CreateBookingDTOTransportType, LCLItemDTO, WaypointBaseDTOTransportMode } from '@/clients/booking'
import {
  getDeliveryAddress,
  getDestinationPort,
  getMainCarriageTransportMode,
  getOnCarriageTransportMode,
  getOriginPort,
  getPickupAddress,
  getPreCarriageTransportMode,
} from '@/features/bookings/utils/waypoints'

import { BookingWizardFormState, LclCargoType } from '../wizard/types/BookingWizardFormState'
import { bookingWizardDefaultBooking, defaultFCLCargo, VGMO_CODE } from '../wizard/utils/bookingCreationUtils'

type MapBookingToBookingWizardFormStateArgs = {
  booking: BookingDTO
  isVgmLocalHandlingServicesEnabled?: boolean
}

// BookingDTO has no cargoLCL; LCL cargo lives in the generic `cargo` array (TransportUnitDTO[]).
const toLclItems = (booking: BookingDTO): LCLItemDTO[] =>
  (booking.cargo ?? []).map((unit) => ({
    quantity: unit.quantity,
    weight: unit.grossWeightKg,
    volume: 0,
    dimensions: unit.dimensions ?? { lengthCm: 0, widthCm: 0, heightCm: 0 },
  }))

export const mapBookingToBookingWizardFormState = ({
  booking,
  isVgmLocalHandlingServicesEnabled = false,
}: MapBookingToBookingWizardFormStateArgs): DefaultValues<BookingWizardFormState> => {
  const waypoints = booking.waypoints ?? []
  const mainCarriageMode = getMainCarriageTransportMode(waypoints)
  const isLcl = booking.transportType === 'lcl'
  const lclItems = isLcl ? toLclItems(booking) : []
  // Legacy bookings carry the isVGMBooking flag; newer ones carry a VGMO local handling product
  const hasVgmWeighingService =
    booking.isVGMBooking === true ||
    (booking.originLocalHandlingService?.products ?? []).some((product) => product.code === VGMO_CODE)

  return {
    ...bookingWizardDefaultBooking,
    clonedFrom: booking.id,
    transportType: isLcl ? CreateBookingDTOTransportType.Lcl : CreateBookingDTOTransportType.Fcl,
    mainCarriageMode: mainCarriageMode ? [mainCarriageMode] : [],
    preCarriageMode: getPreCarriageTransportMode(waypoints) as WaypointBaseDTOTransportMode | undefined,
    onCarriageMode: getOnCarriageTransportMode(waypoints) as WaypointBaseDTOTransportMode | undefined,
    originPort: getOriginPort(waypoints),
    destinationPort: getDestinationPort(waypoints),
    pickupAddressDetails: getPickupAddress(waypoints),
    deliveryAddressDetails: getDeliveryAddress(waypoints),
    cargoFCL: isLcl
      ? undefined
      : {
          ...defaultFCLCargo,
          ...booking.cargoFCL,
          // BookingDTOCargoFCL.packages is optional; keep a default row so the form's packages
          // section renders (otherwise only its "Add More" button shows).
          packages: booking.cargoFCL?.packages?.length ? booking.cargoFCL.packages : defaultFCLCargo.packages,
        },
    cargoLCL: isLcl
      ? {
          isStackableGoods: booking.cargo?.[0]?.isStackableGoods ?? true,
          commodity: booking.cargo?.[0]?.commodity,
          commodityID: booking.cargo?.[0]?.commodityID,
        }
      : bookingWizardDefaultBooking.cargoLCL,
    cargoLCLByDimensionsItems:
      isLcl && lclItems.length ? lclItems : bookingWizardDefaultBooking.cargoLCLByDimensionsItems,
    lclCargoType: LclCargoType.ByDimensions,
    incoterm: booking.incoterm ?? bookingWizardDefaultBooking.incoterm,
    controllingParty: booking.controllingParty ?? bookingWizardDefaultBooking.controllingParty,
    shipperLegalEntityID: booking.shipperLegalEntityID,
    consigneeLegalEntityID: booking.consigneeLegalEntityID,
    onBehalfOfShipperLegalEntityID: booking.onBehalfOfShipperLegalEntityID,
    onBehalfOfConsigneeLegalEntityID: booking.onBehalfOfConsigneeLegalEntityID,
    shipperContactID: booking.shipperContactID,
    consigneeContactID: booking.consigneeContactID,
    onBehalfOfShipperContactID: booking.onBehalfOfShipperContactID,
    onBehalfOfConsigneeContactID: booking.onBehalfOfConsigneeContactID,
    shipper: booking.shipper,
    consignee: booking.consignee,
    importCustomsService: booking.importCustomsService ?? undefined,
    exportCustomsService: booking.exportCustomsService ?? undefined,
    sustainabilityService: booking.sustainabilityService ?? undefined,
    carbonOffsetContribution: booking.carbonOffsetContribution,
    insurance: booking.insurance,
    // Prefill the VGMO service, or the legacy switch while the flag is off
    isVGMBooking: isVgmLocalHandlingServicesEnabled ? undefined : hasVgmWeighingService,
    extraLocalHandlingServicesEnabled: isVgmLocalHandlingServicesEnabled && hasVgmWeighingService,
    originLocalHandlingService: isVgmLocalHandlingServicesEnabled && hasVgmWeighingService ? [VGMO_CODE] : [],
    isBCNBooking: booking.isBCNBooking,
    hasDangerousGoods: booking.hasDangerousGoods ?? (booking.unNumbers?.length ?? 0) > 0,
    unNumbers: booking.unNumbers,
  }
}
